/* eslint-disable */
import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import nodeShape from "./nodeShape";

import Input from "../input/input";
import Icon from "../icon/icon";
import Tooltip from "../tooltip/tooltip";
import Spinner from "../spinner/spinner";

import { ArrowRight } from "../../lib/icons";
import { isFunction, isString, isObject, gatherClasses } from "../../lib/utils";

class TreeNode extends Component {
  constructor(props) {
    super(props);
    this.checkboxRef = React.createRef();
  }

  componentDidMount() {
    this.updateIndeterminateState();
  }

  componentDidUpdate(prevProps) {
    const { checked } = this.props;

    if (prevProps.checked !== checked) {
      this.updateIndeterminateState();
    }
  }

  updateIndeterminateState = () => {
    const { checked } = this.props;
    const checkbox = this.checkboxRef.current;

    if (checkbox) {
      checkbox.indeterminate = checked === 2;
    }
  };

  onCheck = () => {
    let isChecked = false;
    // Toggle off state to checked
    if (this.props.checked === 0) {
      isChecked = true;
    }
    // Toggle partial state based on cascade model
    if (this.props.checked === 2) {
      isChecked = true;
    }
    this.props.onCheck(
      {
        value: this.props.value,
        checked: isChecked,
        [this.props.childrenPropName]: this.props.rawChildren
      },
      this.props.originalNode
    );
  };

  onExpand = () => {
    let isChecked = false;
    if (this.props.checked === 0) {
      isChecked = false;
    } else if (this.props.checked === 1) {
      isChecked = true;
    } else if (this.props.checked === 2) {
      isChecked = true;
    }
    const expanded = !this.props.expanded;
    let loading = false;
    if (expanded) {
      loading = true;
    }
    this.props.onExpand(
      {
        value: this.props.value,
        checked: isChecked,
        expanded: expanded,
        loading: loading,
        halfChecked: this.props.checked === 2
      },
      this.props.originalNode
    );
  };

  hasChildren = () => {
    return this.props.rawChildren !== null;
  };

  renderCollapseButton = () => {
    const { expandDisabled, isLeaf } = this.props;
    if (isLeaf) {
      return (
        <span className="oui-tree-collapse" aria-hidden="true">
          <span className="oui-tree-icon" />
        </span>
      );
    }
    if (this.props.loading) {
      return (
        <span
          aria-label="Loading"
          className="oui-tree-collapse-btn oui-tree-spinner-container"
        >
          <Spinner size="sm" className="oui-tree-spinner" />
        </span>
      );
    }
    return (
      <button
        className="oui-tree-collapse oui-tree-collapse-btn"
        disabled={expandDisabled}
        type="button"
        onClick={this.onExpand}
        tabIndex="-1"
      >
        {this.renderCollapseIcon()}
      </button>
    );
  };

  renderCollapseIcon = () => {
    if (!this.props.expanded) {
      return (
        <Icon
          src={ArrowRight}
          clickable
          className="oui-tree-icon oui-tree-icon-expand-close"
        />
      );
    }
    return (
      <Icon
        src={ArrowRight}
        clickable
        className="oui-tree-icon oui-tree-icon-expand-open"
      />
    );
  };

  renderCheckboxIcon = inputId => {
    const {
      getCheckboxTooltip,
      checked,
      originalNode,
      checkboxTooltipText,
      disabled
    } = this.props;
    let tooltipProps = null;

    const checkbox = (
      <Input
        innerRef={this.checkboxRef}
        type="checkbox"
        checked={checked === 1}
        className="oui-node-checkbox"
        onChange={this.onCheck}
        disabled={disabled}
        id={inputId}
      />
    );

    if (isFunction(getCheckboxTooltip)) {
      const tooltip = getCheckboxTooltip(originalNode);

      if (isString(tooltip)) {
        tooltipProps = { text: tooltip };
      } else if (isObject(tooltip) && tooltip.text) {
        tooltipProps = tooltip;
      }
    }

    if (checkboxTooltipText) {
      tooltipProps = { text: checkboxTooltipText };
    }

    if (tooltipProps) {
      return <Tooltip {...tooltipProps}>{checkbox}</Tooltip>;
    }

    return checkbox;
  };

  renderNodeIcon = () => {
    /* if ( this.props.icon !== null ) {
      return <img src={ icon } alt="icon" />;
    } */
    return this.props.renderIcon(this.props.originalNode);
    /* if ( !this.hasChildren() ) {
      return <span className="oui-tree-icon oui-tree-icon-leaf" />;
    }
    if ( !this.props.expanded ) {
      return <span className="oui-tree-icon oui-tree-icon-parent-close" />;
    }
    return <span className="oui-tree-icon oui-tree-icon-parent-open" />; */
  };

  renderChildren = () => {
    if (!this.props.expanded) {
      return null;
    }
    return this.props.children;
  };

  render() {
    const {
      checkable,
      showHiddenNodes,
      hidden,
      checked,
      className,
      disabled,
      treeId,
      label,
      showNodeIcon,
      value,
      tooltipText,
      expanded,
      level,
      isLeaf,
      nodeHeight
    } = this.props;
    const inputId = `${treeId}-${value}`;
    const nodeClass = classNames(
      {
        "oui-tree-node": true,
        "oui-tree-node-parent": this.hasChildren(),
        "oui-tree-node-leaf": !this.hasChildren(),
        "oui-tree-hiddenNode": hidden,
        hide: hidden && !showHiddenNodes,
        "oui-expanded-node": expanded
      },
      className
    );
    const outerSpanClass = classNames({
      "oui-tree-text": true,
      "oui-selected-node": checked === 1
    });

    let blankSpacesArray = [];
    for (let i = 0; i < level * 8; i++) {
      blankSpacesArray.push(
        <span className="oui-tree-node-empty-space" key={i}>
          &nbsp;
        </span>
      );
    }

    const subLabel = this.props.renderSubLabel(this.props.originalNode);
    const labelComp = isFunction(this.props.renderLabel)
      ? this.props.renderLabel(this.props.originalNode)
      : label;
    const nodeAction = isFunction(this.props.renderNodeAction)
      ? this.props.renderNodeAction(this.props.originalNode)
      : null;
    return (
      <div
        className={nodeClass}
        role="treeitem"
        aria-level={level + 1}
        {...(isLeaf ? {} : { "aria-expanded": level === 0 ? "true" : "false" })}
        data-nodevalue={value}
        tabIndex={this.props.tabIndex}
      >
        <span
          aria-hidden="true"
          style={{ minHeight: nodeHeight + "px" }}
          className={outerSpanClass}
        >
          {blankSpacesArray}
          {this.renderCollapseButton()}
          <label
            htmlFor={inputId}
            style={{ height: nodeHeight - 6 + "px" }}
            className={gatherClasses(
              null,
              "oui-tree-node-label",
              disabled ? "oui-disabled" : ""
            )}
          >
            {checkable && this.renderCheckboxIcon(inputId)}
            {!checkable && (
              <input
                checked={checked === 1}
                disabled={disabled}
                id={inputId}
                type="radio"
                name="treeNodeRadio"
                value={value}
                onChange={this.onCheck}
              />
            )}
            {showNodeIcon ? (
              <span className="oui-tree-node-icon">
                {this.renderNodeIcon()}
              </span>
            ) : null}
            <div className="oui-tree-node-content">
              <div className="oui-tree-title">
                {tooltipText ? (
                  <Tooltip text={tooltipText}>
                    <span>{labelComp}</span>
                  </Tooltip>
                ) : (
                  labelComp
                )}
                {subLabel && (
                  <span className="oui-tree-sub-title">{subLabel}</span>
                )}
              </div>

              {nodeAction && (
                <div className="oui-tree-node-action">{nodeAction}</div>
              )}
            </div>
          </label>
        </span>
        {this.renderChildren()}
      </div>
    );
  }
}

TreeNode.propTypes = {
  isLeaf: PropTypes.bool.isRequired,
  checked: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  hidden: PropTypes.bool,
  expandDisabled: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  showNodeIcon: PropTypes.bool.isRequired,
  treeId: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onCheck: PropTypes.func.isRequired,
  onExpand: PropTypes.func.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  icon: PropTypes.node,
  rawChildren: PropTypes.arrayOf(nodeShape),
  modified: PropTypes.string,
  modifiedLabel: PropTypes.string,
  level: PropTypes.number.isRequired,
  getCheckboxTooltip: PropTypes.func,
  checkboxTooltipText: PropTypes.string
};

TreeNode.defaultProps = {
  children: null,
  className: null,
  icon: null,
  rawChildren: null,
  disabled: false,
  loading: false,
  hidden: false
};

TreeNode.displayName = "OrionTreeNode";

export default TreeNode;
